import { makeAutoObservable } from 'mobx';
import { OsiAccountsWidgetViewModel } from '@shared/types/mobx/widgets/OsiAccountsWidget';
import { OsiAccount } from '@shared/types/osi';
import { inject, injectable } from 'inversify';
import { getErrorText, notifications } from '@shared/utils/notifications';
import logger from 'js-logger';
import { getOsiAccountsByOsiId } from '@shared/api/osi/accounts';
import { AccountFormWithFile, OsiAccountTypes } from '@shared/types/osi/accounts';
import { deleteOsiAccountById } from '@shared/api/osiAccounts/delete';
import { createOsiApplication } from '@shared/api/osiAccountApplications';
import {
  ApplicationTypes,
  OsiAccountApplication,
  OsiAccountApplicationRequestWithFile
} from '@shared/types/osiAccountApplications';
import { IOsiAccountApplicationsWidgetViewModel } from '@shared/types/mobx/widgets/OsiAccountApplications';
import { type RolesService, RolesServiceIdentifier } from '@shared/types/mobx/services/roles';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';

@injectable()
export class OsiAccountsWidgetVm implements OsiAccountsWidgetViewModel {
  accounts: OsiAccount[] = [];

  isLoading = false;

  isCreatingDisabled = true;

  constructor(
    @inject(OsiServiceIdentifier) private osiModule: OsiService,
    @inject(RolesServiceIdentifier) private rolesModule: RolesService
  ) {
    makeAutoObservable(this);

    this.loadAccounts();

    this.isCreatingDisabled = !(this.rolesModule.isHasAdminRole || this.rolesModule.isHasOperatorRole);
  }

  private _applicationsVm: IOsiAccountApplicationsWidgetViewModel | null = null;

  set applicationsVm(value: IOsiAccountApplicationsWidgetViewModel) {
    this._applicationsVm = value;
  }

  loadAccounts = async () => {
    try {
      this.isLoading = true;

      if (!this.osiModule?.osiInfo?.id) return;

      this.accounts = await getOsiAccountsByOsiId(this.osiModule.osiInfo.id);
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };

  addAccount = async (data: AccountFormWithFile) => {
    try {
      if (!this.osiModule.osiId) return;

      const payloadApp: OsiAccountApplicationRequestWithFile = {
        applicationType: ApplicationTypes.ADD,
        osiId: this.osiModule.osiId,
        type: data.accountType as OsiAccountTypes,
        bic: data.bank.bic,
        account: data.account,
        docs: [data.file],
      };

      const result = await createOsiApplication(payloadApp);
    } catch (e) {
      logger.error(e);
      // OSI-806: ошибку пробрасываем наверх — AddAccountButtonFeature покажет её текст
      // и оставит диалог открытым. Раньше ошибка гасилась здесь, поэтому диалог
      // закрывался как при успехе и введённые данные терялись.
      throw e;
    } finally {
      await this.loadAccounts();
      await this._applicationsVm?.refreshApplications();
    }
  };

  editAccount = async (data: AccountFormWithFile & { id: number }) => {
    try {
      if (!this.osiModule.osiId) return;

      const payloadApp: OsiAccountApplicationRequestWithFile = {
        applicationType: ApplicationTypes.UPDATE,
        osiId: this.osiModule.osiId,
        type: data.accountType as OsiAccountTypes,
        bic: data.bank.bic,
        account: data.account,
        osiAccountId: data.id,
        docs: [data.file],
      };

      const result = await createOsiApplication(payloadApp);
    } catch (e) {
      logger.error(e);
      // OSI-806: интерцептор бросает СТРОКУ (`if (code !== 0) throw message`), поэтому
      // прежняя проверка `e instanceof Error` полностью глотала текст ошибки бэка.
      notifications.error(getErrorText(e));
    } finally {
      await this.loadAccounts();
      await this._applicationsVm?.refreshApplications();
    }
  };

  deleteAccount = async (account: OsiAccount) => {
    try {
      if (!account.id) return;
      await deleteOsiAccountById(account.id);
      await this.loadAccounts();
    } catch (e) {
      logger.error(e);
      notifications.error(e);
    }
  };

  openApplicationDocs = async (application: OsiAccountApplication) => {
    await this._applicationsVm?.showApplicationInfo(application);
  };
}
